/**
 * HomeV2 — Glassmorphism Theme
 * Dark: deep indigo/purple + glass panels
 * Light: soft lavender/lilac + translucent glass panels
 */
import { Link } from '@inertiajs/react';
import { useHomeContent } from '@/lib/homeContent';
import { ImageCard, VideoCard, ChatDemo, VoiceDemo, FeatureList, HomeQuickActions, HomeImageTrends, HomeVideoTrends } from './HomeSections';
import { useIsDark } from '@/hooks/useIsDark';

const GradBadge = ({ icon, text, dark }) => (
    <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full border text-sm font-bold uppercase tracking-wider"
        style={{
            background: dark ? 'rgba(124,58,237,0.1)' : 'rgba(124,58,237,0.12)',
            borderColor: dark ? 'rgba(124,58,237,0.3)' : 'rgba(124,58,237,0.4)',
            color: dark ? '#a78bfa' : '#6d28d9',
        }}>
        <span className="material-symbols-outlined text-lg">{icon}</span>{text}
    </div>
);

const isVideoUrl = (url) => /\.(mp4|webm|mov|ogg)(\?.*)?$/i.test(url || '');

export default function HomeV2({ page, openRegister }) {
    const c = useHomeContent(page?.content);
    const dark = useIsDark();
    const heroBg = c.hero_bg || c.intro_video;

    const bg = {
        root: dark ? '#0d0d1a' : '#f5f0ff',
        section1: dark ? 'linear-gradient(to bottom,#0d0d1a,#12121f)' : 'linear-gradient(to bottom,#ede9fe,#f5f0ff)',
        section2: dark ? '#12121f' : '#faf8ff',
        section3: dark ? '#0d0d1a' : '#ede9fe',
        cta: dark ? 'linear-gradient(135deg,#4c1d95,#1e1b4b,#7c2d12)' : 'linear-gradient(135deg,#6d28d9,#4c1d95,#7c3aed)',
    };
    const text = dark ? 'text-white' : 'text-slate-900';
    const muted = dark ? 'text-white/60' : 'text-slate-600';
    const heroText = dark ? 'text-white' : 'text-slate-950';
    const heroMuted = dark ? 'text-white/60' : 'text-slate-700';
    const glass = dark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.6)';
    const glassBorder = dark ? 'rgba(255,255,255,0.1)' : 'rgba(124,58,237,0.2)';

    return (
        <div className="min-h-screen" style={{ background: bg.root }}>
            {c.custom_css && <style dangerouslySetInnerHTML={{ __html: c.custom_css }} />}

            {/* ── Hero ─────────────────────────────────────────── */}
            <section className="relative min-h-[90vh] flex items-center overflow-hidden" style={{ background: bg.root }}>
                {/* Hero background */}
                {heroBg && (
                    isVideoUrl(heroBg) ? (
                        <video
                            key={heroBg}
                            className="absolute inset-0 w-full h-full object-cover"
                            autoPlay
                            loop
                            muted
                            playsInline
                            style={{ opacity: dark ? 0.48 : 0.28 }}
                        >
                            <source src={heroBg} type="video/mp4" />
                        </video>
                    ) : (
                        <img
                            src={heroBg}
                            alt="Hero background"
                            className="absolute inset-0 w-full h-full object-cover mix-blend-overlay"
                            style={{ opacity: dark ? 0.42 : 0.22 }}
                        />
                    )
                )}
                <div className="absolute inset-0 dark:hidden" style={{ background: 'linear-gradient(180deg,rgba(255,255,255,0.18),rgba(245,240,255,0.08) 36%,rgba(245,240,255,0.72) 100%)' }} />
                <div className="absolute inset-0 dark:hidden" style={{ background: 'linear-gradient(90deg,rgba(255,255,255,0.42),rgba(255,255,255,0.08) 40%,transparent 75%)' }} />
                <div className="absolute inset-0" style={{ background: dark ? 'radial-gradient(ellipse at top left,rgba(139,92,246,0.3),transparent 60%)' : 'radial-gradient(ellipse at top left,rgba(139,92,246,0.2),transparent 60%)' }} />
                <div className="absolute inset-0" style={{ background: dark ? 'radial-gradient(ellipse at bottom right,rgba(255,123,0,0.2),transparent 60%)' : 'radial-gradient(ellipse at bottom right,rgba(255,123,0,0.12),transparent 60%)' }} />
                <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full" style={{ background: dark ? 'rgba(139,92,246,0.2)' : 'rgba(139,92,246,0.12)', filter: 'blur(120px)' }} />
                <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full" style={{ background: dark ? 'rgba(255,123,0,0.15)' : 'rgba(255,123,0,0.1)', filter: 'blur(100px)' }} />

                <div className="relative z-10 max-w-[1200px] mx-auto px-6 py-24 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                    <div>
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-8 text-sm font-bold uppercase tracking-wider"
                            style={{ background: glass, backdropFilter: 'blur(12px)', borderColor: glassBorder, color: dark ? '#a78bfa' : '#6d28d9' }}>
                            ✨ {c.hero_small_title}
                        </div>
                        <h1 className={`mb-8 text-6xl font-black leading-tight ${heroText} md:text-7xl`}
                            dangerouslySetInnerHTML={{ __html: c.hero_main_title }} />
                        <p className={`mb-12 text-xl leading-relaxed ${heroMuted}`}>{c.hero_description}</p>
                        <div className="flex gap-4 flex-wrap">
                            {c.hero_btn_1_url ? (
                                <a href={c.hero_btn_1_url} className="px-10 py-4 rounded-2xl font-bold text-lg text-white transition-all hover:scale-105"
                                    style={{ background: 'linear-gradient(135deg,#7c3aed,#ff7b00)', boxShadow: '0 20px 60px rgba(124,58,237,0.4)' }}>
                                    {c.hero_btn_1_text}
                                </a>
                            ) : (
                                <button onClick={openRegister} className="px-10 py-4 rounded-2xl font-bold text-lg text-white transition-all hover:scale-105"
                                    style={{ background: 'linear-gradient(135deg,#7c3aed,#ff7b00)', boxShadow: '0 20px 60px rgba(124,58,237,0.4)' }}>
                                    {c.hero_btn_1_text}
                                </button>
                            )}
                            <Link href={c.hero_btn_2_url} className={`px-10 py-4 rounded-2xl font-bold text-lg transition-all hover:scale-105 border ${text}`}
                                style={{ background: glass, backdropFilter: 'blur(12px)', borderColor: glassBorder }}>
                                {c.hero_btn_2_text}
                            </Link>
                        </div>
                    </div>
                    {/* Video preview panel */}
                    <div className="hidden lg:block relative">
                        <div className="absolute -inset-4 rounded-3xl blur-3xl" style={{ background: 'linear-gradient(135deg,rgba(124,58,237,0.2),rgba(255,123,0,0.15))' }} />
                        <div className="relative rounded-3xl overflow-hidden aspect-video border"
                            style={{ background: glass, backdropFilter: 'blur(20px)', borderColor: glassBorder }}>
                            <video className="w-full h-full object-cover" autoPlay loop muted playsInline>
                                <source src={c.intro_video} type="video/mp4" />
                            </video>
                            {/* Glass overlay label */}
                            <div className="absolute bottom-4 left-4 right-4 rounded-2xl px-4 py-3 flex items-center gap-3"
                                style={{ background: glass, backdropFilter: 'blur(12px)', borderColor: glassBorder, border: `1px solid ${glassBorder}` }}>
                                <span className="material-symbols-outlined text-[20px]" style={{ color: dark ? '#a78bfa' : '#7c3aed' }}>auto_awesome</span>
                                <span className={`text-sm font-semibold ${text}`}>AI-Powered Creative Studio</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Stats ─────────────────────────────────────────── */}
            <section className="py-16" style={{ background: bg.section1 }}>
                <div className="max-w-[1000px] mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[1, 2, 3, 4].map(i => (
                        <div key={i} className="rounded-2xl p-6 text-center transition-all hover:scale-105 group border"
                            style={{ background: glass, backdropFilter: 'blur(10px)', borderColor: glassBorder }}>
                            <div className="text-4xl font-black text-transparent bg-clip-text mb-2 group-hover:scale-105 transition-transform" style={{ backgroundImage: 'linear-gradient(135deg,#7c3aed,#ff7b00)' }}>{c[`stat_number_${i}`]}</div>
                            <div className={`text-xs uppercase tracking-wider ${muted}`}>{c[`stat_label_${i}`]}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* ── Intro ─────────────────────────────────────────── */}
            {c.show_intro !== false && (
                <section className="py-24" style={{ background: bg.section2 }}>
                    <div className="max-w-[1200px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
                        <div className="space-y-8 order-2 lg:order-1">
                            <GradBadge icon="auto_awesome" text={c.intro_badge} dark={dark} />
                            <h2 className={`text-5xl md:text-6xl font-black ${text}`} dangerouslySetInnerHTML={{ __html: c.intro_title }} />
                            <p className={`text-xl leading-relaxed ${muted}`} dangerouslySetInnerHTML={{ __html: c.intro_desc }} />
                            <div className="flex flex-wrap gap-6">
                                {[c.intro_check_1, c.intro_check_2].filter(Boolean).map((t, i) => (
                                    <div key={i} className={`flex items-center gap-3 ${dark ? 'text-white/80' : 'text-slate-700'}`}>
                                        <span className="material-symbols-outlined text-2xl" style={{ color: dark ? '#a78bfa' : '#7c3aed' }}>check_circle</span>
                                        <span className="font-semibold">{t}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="relative order-1 lg:order-2">
                            <div className="absolute -inset-4 rounded-3xl blur-3xl" style={{ background: 'linear-gradient(135deg,rgba(124,58,237,0.15),rgba(255,123,0,0.15))' }} />
                            <div className="relative rounded-3xl overflow-hidden aspect-video border" style={{ borderColor: 'rgba(124,58,237,0.3)' }}>
                                <video className="absolute inset-0 w-full h-full object-cover" autoPlay loop muted playsInline>
                                    <source src={c.intro_video} type="video/mp4" />
                                </video>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* ── Quick Actions ────────────────────────────────────────── */}
            {c.show_quick_actions !== false && (
                <section className="py-24" style={{ background: bg.section3 }}>
                    <div className="max-w-[1400px] mx-auto px-6 space-y-12">
                        <div className="text-center space-y-4 max-w-3xl mx-auto">
                            <GradBadge icon="bolt" text={c.quick_actions_badge || 'AI Models'} dark={dark} />
                            <h2 className={`text-5xl md:text-6xl font-black ${text}`} dangerouslySetInnerHTML={{ __html: c.quick_actions_title || 'Explore AI Capability' }} />
                            <p className={`text-lg ${muted}`}>{c.quick_actions_desc || 'Access all premium generation models and tools instantly from your dashboard.'}</p>
                        </div>
                        <HomeQuickActions />
                    </div>
                </section>
            )}

            {/* ── Image Trends ──────────────────────────────────────── */}
            {c.show_trends !== false && (
                <section className="py-24" style={{ background: bg.section3 }}>
                    <div className="max-w-[1400px] mx-auto px-6 space-y-12">
                        <div className="text-center space-y-4 max-w-3xl mx-auto">
                            <GradBadge icon="auto_awesome" text={c.trends_badge || 'Trending'} dark={dark} />
                            <h2 className={`text-5xl md:text-6xl font-black ${text}`} dangerouslySetInnerHTML={{ __html: c.trends_title || 'Discover <span class="text-primary">AI Image Trends</span>' }} />
                            <p className={`text-lg ${muted}`}>{c.trends_desc || 'Explore our most popular AI templates and creative presets.'}</p>
                        </div>
                        <HomeImageTrends />
                    </div>
                </section>
            )}

            {/* ── Image Studio ──────────────────────────────────── */}
            {c.show_images !== false && (
                <section className="py-24" style={{ background: bg.section3 }}>
                    <div className="max-w-[1200px] mx-auto px-6 space-y-16">
                        <div className="text-center space-y-6 max-w-3xl mx-auto">
                            <GradBadge icon="image" text={c.image_studio_badge} dark={dark} />
                            <h2 className={`text-5xl md:text-6xl font-black ${text}`} dangerouslySetInnerHTML={{ __html: c.image_studio_title }} />
                            <p className={`text-lg ${muted}`}>{c.image_studio_desc}</p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                            {c.image_cards.map((item, i) => (
                                <ImageCard key={i} item={item}
                                    cardClass={`border-violet-${dark ? '900' : '300'}/30 ${dark ? 'bg-[#12121f]' : 'bg-white/60'}`}
                                    overlayClass={dark ? 'bg-black/30' : 'bg-white/30'} />
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* ── Video Trends ──────────────────────────────────── */}
            {c.show_video_trends !== false && (
                <section className="py-24" style={{ background: bg.section2 }}>
                    <div className="max-w-[1400px] mx-auto px-6 space-y-16">
                        <div className="text-center space-y-6 max-w-3xl mx-auto">
                            <GradBadge icon="smart_display" text={c.video_trends_badge || 'Video Trends'} dark={dark} />
                            <h2 className={`text-5xl md:text-6xl font-black ${text}`} dangerouslySetInnerHTML={{ __html: c.video_trends_title || 'Trending <span class="text-primary">Avatar Templates</span>' }} />
                            <p className={`text-lg ${muted}`}>{c.video_trends_desc || 'Preview popular avatar video templates before creating your own.'}</p>
                        </div>
                        <HomeVideoTrends />
                    </div>
                </section>
            )}

            {/* ── Video Studio ──────────────────────────────────── */}
            {c.show_videos !== false && (
                <section className="py-24" style={{ background: bg.section2 }}>
                    <div className="max-w-[1200px] mx-auto px-6 space-y-16">
                        <div className="text-center space-y-6 max-w-3xl mx-auto">
                            <GradBadge icon="movie" text={c.video_studio_badge} dark={dark} />
                            <h2 className={`text-5xl md:text-6xl font-black ${text}`} dangerouslySetInnerHTML={{ __html: c.video_studio_title }} />
                            <p className={`text-lg ${muted}`}>{c.video_studio_desc}</p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                            {c.video_cards.map((item, i) => (
                                <VideoCard key={i} item={item} cardClass={`${dark ? 'bg-[#0d0d1a]' : 'bg-white/60'} border-0`} />
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* ── Chat ──────────────────────────────────────────── */}
            {c.show_chat !== false && (
                <section className="py-24" style={{ background: bg.section3 }}>
                    <div className="max-w-[1200px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        <div className="space-y-8 order-2 lg:order-1">
                            <GradBadge icon="chat" text={c.chat_badge} dark={dark} />
                            <h2 className={`text-5xl md:text-6xl font-black ${text}`} dangerouslySetInnerHTML={{ __html: c.chat_title }} />
                            <p className={`text-xl leading-relaxed ${muted}`}>{c.chat_desc}</p>
                            <FeatureList items={c.chat_features} textClass={dark ? 'text-white/80' : 'text-slate-700'} />
                        </div>
                        <div className="relative order-1 lg:order-2">
                            <div className="absolute -inset-4 rounded-3xl blur-3xl" style={{ background: 'linear-gradient(135deg,rgba(124,58,237,0.1),rgba(255,123,0,0.1))' }} />
                            <ChatDemo panelClass={`border-violet-${dark ? '900' : '300'}/30`} style={{ background: glass, backdropFilter: 'blur(20px)' }} />
                        </div>
                    </div>
                </section>
            )}

            {/* ── Voice ─────────────────────────────────────────── */}
            {c.show_voice !== false && (
                <section className="py-24" style={{ background: bg.section2 }}>
                    <div className="max-w-[1200px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        <div className="relative">
                            <div className="absolute -inset-4 rounded-3xl blur-3xl" style={{ background: 'linear-gradient(135deg,rgba(124,58,237,0.1),rgba(255,123,0,0.1))' }} />
                            <VoiceDemo panelClass={`border-violet-${dark ? '900' : '300'}/30`} />
                        </div>
                        <div className="space-y-8">
                            <GradBadge icon="graphic_eq" text={c.voice_badge} dark={dark} />
                            <h2 className={`text-5xl md:text-6xl font-black ${text}`} dangerouslySetInnerHTML={{ __html: c.voice_title }} />
                            <p className={`text-xl leading-relaxed ${muted}`}>{c.voice_desc}</p>
                            <FeatureList items={c.voice_features} textClass={dark ? 'text-white/80' : 'text-slate-700'} />
                        </div>
                    </div>
                </section>
            )}

            {/* ── CTA ───────────────────────────────────────────── */}
            <section className="py-32 relative overflow-hidden" style={{ background: bg.cta }}>
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(124,58,237,0.3)_0%,transparent_70%)]" />
                <div className="relative z-10 max-w-[700px] mx-auto px-6 text-center">
                    <h2 className="text-6xl font-black text-white mb-6">{c.cta_title} <span className="text-primary">{c.cta_subtitle}</span>?</h2>
                    <p className="text-white/60 text-xl mb-12">{c.cta_desc}</p>
                    <button onClick={openRegister} className="px-16 py-6 rounded-2xl font-black text-xl text-white hover:scale-105 transition-all"
                        style={{ background: 'linear-gradient(135deg,#7c3aed,#ff7b00)', boxShadow: '0 30px 80px rgba(124,58,237,0.5)' }}>
                        {c.cta_btn_primary}
                    </button>
                    <p className="text-white/30 text-sm mt-6">{c.cta_tagline}</p>
                </div>
            </section>
        </div>
    );
}
